<app-nav></app-nav>
<div class="hra-container">
  <div class="card main-card">
    <div class="card-body">

      <!-- Header with Screen Title -->
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4">
        <h1 class="h2 mb-3 mb-md-0 font-weight-bold">{{getScreenTitle()}}</h1>
        <!-- Show only dashboard button when on form or review screen -->
        <div class="btn-group" role="group" *ngIf="screen === 'form' || screen === 'review'">
          <button (click)="setScreen('dashboard')" class="btn btn-outline-primary">
            <i class="fas fa-arrow-left mr-1"></i> Dashboard
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div *ngIf="isLoading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading...</p>
      </div>

      <!-- Form Screen -->
      <div *ngIf="screen === 'form' && !isLoading">
        <!-- Applicant Details -->
        <section class="mb-5">
          <h2 class="h5 font-weight-semibold mb-3">Applicant Details</h2>
          <div class="row g-3">
            <div class="col-12 col-md-3 mb-2">
              <input [(ngModel)]="personal.name" placeholder="Full name" class="form-control"
                [disabled]="isInputDisabled" />
            </div>
            <div class="col-12 col-md-2 mb-2">
              <input [(ngModel)]="personal.age" placeholder="Age" class="form-control" type="number"
                [disabled]="isInputDisabled" />
            </div>
            <div class="col-12 col-md-3 mb-2">
              <select [(ngModel)]="personal.gender" class="form-control" [disabled]="isInputDisabled">
                <option value="">Gender</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
            <div class="col-12 col-md-4">
              <select [(ngModel)]="personal.relation" class="form-control" [disabled]="isInputDisabled">
                <option value="">Relation</option>
                <option *ngFor="let relation of getRelationOptions()" [value]="relation">
                  {{relation}}
                </option>
              </select>
            </div>
          </div>
        </section>

        <!-- Categories -->
        <div *ngFor="let category of categories">
          <section class="mb-5">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h3 class="h6 font-weight-semibold mb-0">
                {{category.title}}
                <small class="text-muted">(Max {{category.max}} pts)</small>
              </h3>
              <div class="text-muted small">
                Score: {{calculateCategoryScore(category)}}
              </div>
            </div>

            <div class="row g-3">
              <div *ngFor="let question of category.questions" class="col-12 mb-4">
                <div class="card border question-card">
                  <div class="card-body">
                    <div class="font-weight-medium mb-3">{{question.label}}</div>
                    <div class="d-flex flex-wrap gap-3">
                      <div *ngFor="let option of question.options; let i = index" class="form-check form-check-inline">
                        <input class="form-check-input" type="radio" [name]="question.id" [id]="question.id + '_' + i"
                          [checked]="answers[question.id] === option.score"
                          (change)="setAnswer(question.id, option.score)" />
                        <label class="form-check-label" [for]="question.id + '_' + i">
                          {{option.label}}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Form Actions -->
        <div class="d-flex justify-content-end gap-2">
          <button (click)="setScreen('dashboard')" class="btn btn-outline-secondary">
            Cancel
          </button>
          <button (click)="setScreen('review')" class="btn btn-primary px-4 py-2" [disabled]="!isFormComplete()">
            View Health Score
          </button>
        </div>
      </div>

      <!-- Review Screen -->
      <div *ngIf="screen === 'review' && !isLoading">
        <h2 class="h5 font-weight-semibold mb-4">
          <span>Applicant Details</span>
          <!-- <span *ngIf="isViewingPreviousReport">Previous Health Report - {{personal.name}}</span> -->
        </h2>

        <!-- Applicant Details Summary -->
        <div class="applicant-summary-card mb-4">
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="summary-item">
                  <div class="summary-label">Name</div>
                  <div class="summary-value">{{personal.name}}</div>
                </div>
              </div>
              <div class="col-md-2">
                <div class="summary-item">
                  <div class="summary-label">Age</div>
                  <div class="summary-value">{{personal.age}}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="summary-item">
                  <div class="summary-label">Gender</div>
                  <div class="summary-value">{{personal.gender}}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="summary-item">
                  <div class="summary-label">Relation</div>
                  <div class="summary-value">{{personal.relation}}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row g-4 mb-5">
          <!-- Show only one card when viewing previous report -->
          <div *ngIf="isViewingPreviousReport" class="col-12">
            <div class="card border shadow-sm h-100 current-score-card">
              <div class="card-header">
                <h6 class="mb-0">Previous Assessment Score</h6>
              </div>
              <div class="card-body d-flex flex-column align-items-center justify-content-center">
                <div class="text-muted small mb-1">Previous Total Score</div>
                <div class="display-4 font-weight-bold mb-2">{{totalScore}} / 100</div>
                <div [class]="riskLevel.className + ' font-weight-semibold risk-badge'">
                  {{riskLevel.label}}
                </div>
                <div class="text-muted small mt-3">
                  <i class="fas fa-calendar-alt mr-1"></i>
                  {{getCurrentUserPreviousScoreDate() | date:'mediumDate'}}
                </div>
              </div>
            </div>
          </div>

          <!-- Show current assessment with optional previous score card -->
          <ng-container *ngIf="!isViewingPreviousReport">
            <!-- Current Score Card - Full width when not showing previous card -->
            <div [class]="showPreviousScoreCard ? 'col-12 col-md-8' : 'col-12'">
              <div class="card border shadow-sm h-100 current-score-card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Current Assessment</h6>
                  <!-- Show "View Previous Score" link only if user has previous score - Off-white hyperlink -->
                  <a *ngIf="hasPreviousScore() && !showPreviousScoreCard" 
                     href="javascript:void(0)" 
                     class="view-previous-score-link"
                     (click)="togglePreviousScoreCard()">
                      View Previous Score
                  </a>
                  <!-- Show "Close" link when previous card is visible -->
                </div>
                <div class="card-body d-flex flex-column align-items-center justify-content-center">
                  <div class="text-muted small mb-1">Total Current Score</div>
                  <div class="display-4 font-weight-bold mb-2">{{totalScore}} / 100</div>
                  <div [class]="riskLevel.className + ' font-weight-semibold risk-badge'">
                    {{riskLevel.label}}
                  </div>
                  <div class="text-muted small mt-3">
                    <i class="fas fa-calendar-alt mr-1"></i>
                    {{today | date:'mediumDate'}}
                  </div>
                </div>
              </div>
            </div>

            <!-- Previous Score Card - Shown only when toggled -->
            <div *ngIf="showPreviousScoreCard" class="col-12 col-md-4">
              <div class="card border shadow-sm h-100 previous-score-card" style="background-color: #f8f9fa; border-color: #dee2e6;">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Previous Score</h6>
                  <a href="javascript:void(0)" class="small" (click)="togglePreviousScoreCard()">
                    <i class="fas fa-times" style="color: #ffffff;"></i>
                  </a>
                </div>
                <div class="card-body d-flex flex-column align-items-center justify-content-center">
                  <div class="small text-muted mb-1">Previous Total Score</div>
                  <div class="display-4 font-weight-bold mb-2" style="color: #6c757d;">{{getCurrentUserPreviousScore()}} / 100</div>
                  <div [class]="getCurrentUserPreviousRiskLevel().className + ' font-weight-semibold risk-badge'" 
                       style="opacity: 0.7;">
                    {{getCurrentUserPreviousRiskLevel().label}}
                  </div>
                  <div class="small text-muted mt-3">
                    <i class="fas fa-calendar-alt mr-1"></i>
                    {{getCurrentUserPreviousScoreDate() | date:'mediumDate'}}
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- Category Scores with Recommendations -->
        <div class="card border mb-5">
          <div class="card-header">
            <h6 *ngIf="isViewingPreviousReport" class="mb-0">Category-wise Recommendations for Previous Assessment</h6>
            <h6 *ngIf="!isViewingPreviousReport" class="mb-0">Category-wise Recommendations for Current Assessment</h6>
          </div>
          <div class="card-body">
            <div class="row g-3" *ngIf="totalScore !== 0; else noScoreMessage">
              <div *ngFor="let categoryKey of getCategoryKeys()" class="col-12 col-md-6">
                <div class="card border mb-4 suggestion-card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <div class="font-weight-semibold text-capitalize">{{getCategoryTitle(categoryKey)}}</div>
                      <div class="text-muted small">
                        Score: {{getCategoryScore(categoryKey).score}} / {{getCategoryScore(categoryKey).max}}
                      </div>
                    </div>

                    <!-- Recommendations -->
                    <div *ngIf="shouldShowImprovement(categoryKey)" class="mt-3">
                      <h6 class="font-weight-semibold mb-2 text-danger">Suggestions to improve:</h6>
                      <ul class="list-unstyled mb-0">
                        <li *ngFor="let rec of getFilteredRecommendations(categoryKey); let i = index"
                          class="small text-dark mb-1">
                          • {{rec}}
                        </li>
                      </ul>
                    </div>
                    <div *ngIf="!shouldShowImprovement(categoryKey)" class="mt-3">
                      <div class="alert alert-success mb-0">
                        <i class="fas fa-check-circle mr-2"></i>
                        {{getCategoryTitle(categoryKey)}} is fine - no further action required.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ng-template #noScoreMessage>
              <div class="alert alert-info mb-0" role="alert">
                <i class="fas fa-info-circle mr-2"></i>
                Visit the Dashboard tab to avail a new assessment.
              </div>
            </ng-template>
          </div>
        </div>

        <!-- Review Actions -->
        <div class="d-flex flex-column flex-md-row justify-content-between">
          <button (click)="setScreen('form')" class="btn btn-outline-secondary mb-2 mb-md-0"
            *ngIf="!isViewingPreviousReport">
            <i class="fas fa-edit mr-1"></i> Edit
          </button>
          <button (click)="setScreen('dashboard')" class="btn btn-outline-secondary mb-2 mb-md-0"
            *ngIf="isViewingPreviousReport">
            <i class="fas fa-arrow-left mr-1"></i> Back to Dashboard
          </button>
          <!-- <div class="d-flex gap-2"> -->
            <button (click)="saveAssessment()" class="btn btn-success" [disabled]="isLoading || isViewingPreviousReport || disableSaveButton"
              *ngIf="!isViewingPreviousReport">
              <i class="fas fa-save mr-1"></i> Save
            </button>
            <button (click)="startNewAssessment(selectedUserForEdit)" class="btn btn-primary"
              *ngIf="isViewingPreviousReport">
              <i class="fas fa-redo-alt mr-1"></i> Reassess Health Risk
            </button>
          <!-- </div> -->
        </div>

        <!-- Save Message -->
        <div *ngIf="saveMessage" class="alert alert-success mt-3" role="alert" style="font-size: 1.1rem;">
          <i class="fas fa-check-circle mr-2"></i>
          {{saveMessage}}
        </div>
      </div>

      <!-- Dashboard Screen -->
      <div *ngIf="screen === 'dashboard' && !isLoading" class="dashboard-screen">
        <!-- <h2 class="h5 font-weight-semibold mb-4">Previous Assessments</h2> -->

        <!-- Message if no data -->
        <div *ngIf="userAssessments.length === 0" class="alert alert-info">
          <i class="fas fa-info-circle mr-2"></i>
          No assessment data was found. Please verify that you have an active policy with us.
        </div>

        <!-- User Assessment Cards Grid -->
        <div class="user-cards-grid">
          <div *ngFor="let user of userAssessments" class="user-card">
            <div class="card-header">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0 text-truncate" style="max-width: 200px;">
                  <i class="fas fa-user mr-2"></i>
                  {{user.name}}
                </h5>
                <span class="badge badge-primary">{{user.relation}}</span>
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Left Side: User Details -->
                <div class="col-md-6">
                  <div class="user-info">
                    <div class="info-item mb-3">
                      <div class="info-label">Name</div>
                      <div class="info-value text-truncate" style="max-width: 150px;">{{user.name}}</div>
                    </div>
                    <div class="info-item mb-3">
                      <div class="info-label">Age</div>
                      <div class="info-value">{{user.age}} years</div>
                    </div>
                    <div class="info-item mb-3">
                      <div class="info-label">Gender</div>
                      <div class="info-value">{{user.gender}}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Relation</div>
                      <div class="info-value">{{user.relation}}</div>
                    </div>
                  </div>
                </div>

                <!-- Right Side: Previous Score -->
                <div class="col-md-6">
                  <div class="score-card">
                    <div *ngIf="user.previousScore; else noScore">
                      <div class="score-header mb-3">
                        <h6 class="score-title mb-0">Previous Score</h6>
                      </div>
                      <div class="score-content">
                        <div class="score-value mb-2">
                          {{ user.previousScore.totalScore }} / 100
                        </div>
                        <div class="risk-level mb-2" [ngClass]="user.previousScore.riskLevel.className">
                          {{ user.previousScore.riskLevel.label }}
                        </div>
                        <div class="score-date">
                          <small>Date: {{ user.previousScore.assessmentDate | date:'mediumDate' }}</small>
                        </div>
                      </div>
                    </div>
                    <ng-template #noScore>
                      <div class="score-content">
                        <div class="score-value mb-2" style="color: #6c757d; font-size: 1rem;">
                          No Previous Score
                        </div>
                        <div class="text-muted small mb-3">
                          Complete first assessment to view score
                        </div>
                      </div>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Updated Card Footer with exact button heights -->
            <div class="card-footer">
              <div *ngIf="user.previousScore; else assessHealthButton">
                <!-- Show two buttons when previous score exists -->
                <div class="button-container">
                  <button (click)="viewPreviousReport(user)" class="btn btn-success btn-sm full-report-btn">
                    <i class="fas fa-file-medical-alt mr-1"></i> Access Full Report
                  </button>
                  <div class="d-flex justify-content-end mt-3">
                    <button (click)="startNewAssessment(user)" class="btn btn-outline-primary btn-sm reassess-btn">
                       Reassess Health Risk
                    </button>
                  </div>
                </div>
              </div>
              <ng-template #assessHealthButton>
                <!-- Show only one button when no previous score - exact same height as two buttons -->
                <div class="single-button-container">
                  <button (click)="startNewAssessment(user)" class="btn btn-outline-primary btn-sm single-assess-btn">
                    <i class="fas fa-plus-circle mr-1"></i> Assess Health Risk
                  </button>
                </div>
              </ng-template>
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</div>